import * as Yup from 'yup'
import { Field, Form, Formik } from 'formik'
import { has, isEmpty, omitBy, omit } from 'lodash'
import { toast } from 'react-toastify'
import { useCallback, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'

import Modal from 'components/Modal'
import RadioButton from 'components/RadioButton'
import TextBox from 'components/TextBox'
import { SpySidebar } from 'components/shared/SpySidebar'
import { discountableOptions, taxesOptions } from 'sharedConstants'
import { updateGlobalItem } from 'store/Items/actions'

const BulkForm = () => {
  const [taxOptions, setTaxOptions] = useState('')
  const [discountableOption, setDiscountableOption] = useState('')
  const history = useHistory()
  const { itemsSelected } = history.location.state
  const sectionRefs = [useRef(null), useRef(null), useRef(null)]
  const dispatch = useDispatch()
  const returnToItemsView = () => history.goBack()
  const spySidebarSettings = [
    {
      ref: sectionRefs[0],
      text: 'price',
    },
    {
      ref: sectionRefs[1],
      text: 'tax',
    },
    {
      ref: sectionRefs[2],
      text: 'Discounts',
    },
  ]

  const initialValues = {
    price: '',
    rate: '',
    taxable: false,
    taxInclusive: false,
  }

  const validationSchema = Yup.object().shape({
    price: Yup.number().min(0),
  })

  const disableUpdateButton = (dirty, errors) => {
    const disableButton = !dirty || !isEmpty(errors)

    return disableButton
  }

  const updateItem = useCallback(
    (itemParams) => {
      dispatch(updateGlobalItem(itemParams))
    },
    [dispatch]
  )

  const onSubmit = async (values, resetForm) => {
    try {
      let itemParams = []

      const emptyValues = omitBy(values, (value) => value === '')
      // we cannot send unchanged radiobuttons, so we have to remove them
      // from array which we want to send
      // if we do not filter these data, we will overwrite current data with default values
      // from initialValues

      const valuesWithFilteredRadiobuttons =
        taxOptions === '' || has(emptyValues, 'clearTaxes')
          ? omit(emptyValues, [
              'taxable',
              'taxInclusive',
              'clearTaxes',
            ])
          : emptyValues

      itemsSelected.forEach(async (uuid) => {
        itemParams = [
          ...itemParams,
          { uuid, ...valuesWithFilteredRadiobuttons },
        ]
      })
      await updateItem(itemParams)
    } catch (error) {
      console.error(error)
    } finally {
      toast.success('Items settings updated successfully!!!')
      resetForm(initialValues)
      setTaxOptions('')
      setDiscountableOption('')
    }
  }

  if (isEmpty(itemsSelected)) history.goBack()

  return (
    <Modal title="Bulk Edit" onClose={returnToItemsView}>
      <div className="modal-body">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={(values, { resetForm }) =>
            onSubmit(values, resetForm)
          }
          validationSchema={validationSchema}
        >
          {({ dirty, errors, setFieldValue }) => (
            <Form>
              <div className="bulk-settings-wrapper">
                <div className="bulk-settings">
                  <section className="bulk-settings__section__info">
                    <span className="bulk-settings__section__info__amount">
                      {itemsSelected?.length}
                    </span>
                    <p className="bulk-settings__section__info__text">
                      Items Selected
                    </p>
                  </section>
                  <section
                    ref={sectionRefs[0]}
                    id={spySidebarSettings[0].text}
                    className="bulk-settings__section__price "
                  >
                    <Field
                      component={TextBox}
                      label="Price"
                      name="price"
                      type="number"
                    />
                  </section>
                  <section
                    ref={sectionRefs[1]}
                    id={spySidebarSettings[1].text}
                    className="bulk-settings__section"
                  >
                    <div className="content-wrapper">
                      <span className="content-value">
                        <Field
                          component={RadioButton}
                          name="taxable"
                          placeholder="Make All Non-Taxable"
                          type="radio"
                          checked={taxOptions === 'Non-Taxable'}
                          onClick={() => {
                            setTaxOptions(taxesOptions.NON_TAXABLE)
                            setFieldValue('taxable', false)
                            setFieldValue('taxInclusive', false)
                          }}
                        />
                      </span>
                    </div>
                    <div className="content-wrapper">
                      <span className="content-value">
                        <Field
                          component={RadioButton}
                          name="taxable"
                          placeholder="Make All Add-on Tax"
                          type="radio"
                          checked={taxOptions === 'Tax Added On'}
                          onClick={() => {
                            setTaxOptions(taxesOptions.TAXABLE)
                            setFieldValue('taxable', true)
                            setFieldValue('taxInclusive', false)
                          }}
                        />
                      </span>
                    </div>
                    <div className="content-wrapper">
                      <span className="content-value">
                        <Field
                          component={RadioButton}
                          name="taxInclusive"
                          placeholder="Make All Tax Inclusive"
                          type="radio"
                          checked={taxOptions === 'Tax Inclusive'}
                          onClick={(value) => {
                            setTaxOptions(taxesOptions.TAX_INCLUSIVE)
                            setFieldValue('taxInclusive', value)
                          }}
                        />
                      </span>
                    </div>
                    <div className="content-wrapper">
                      <span className="content-value">
                        <Field
                          component={RadioButton}
                          name="clearTaxes"
                          placeholder="No Tax Updates"
                          type="radio"
                          checked={taxOptions === 'No Tax Updates'}
                          onClick={() => {
                            setTaxOptions(taxesOptions.NO_TAX_UPDATES)
                            setFieldValue('taxable', '')
                            setFieldValue('taxInclusive', '')
                          }}
                        />
                      </span>
                    </div>
                    {['Tax Inclusive', 'Tax Added On'].includes(
                      taxOptions
                    ) && (
                      <div className="content-wrapper">
                        <Field
                          name="rate"
                          label="Tax Rate"
                          component={TextBox}
                          type="number"
                          minValue="0.00001"
                        />
                      </div>
                    )}
                  </section>
                  <section
                    ref={sectionRefs[2]}
                    id={spySidebarSettings[2].text}
                    className="bulk-settings__section"
                  >
                    <div className="content-wrapper">
                      <h3 className="header-title">Discounts</h3>
                    </div>
                    <div className="content-wrapper">
                      <span className="content-value">
                        <Field
                          component={RadioButton}
                          name="discountable"
                          placeholder="Make All Discountable"
                          type="radio"
                          checked={
                            discountableOption === 'All Discountable'
                          }
                          onClick={() => {
                            setDiscountableOption(
                              discountableOptions.ALL_DISCOUNTABLE
                            )
                            setFieldValue('discountable', true)
                          }}
                        />
                      </span>
                    </div>
                    <div className="content-wrapper">
                      <span className="content-value">
                        <Field
                          component={RadioButton}
                          name="discountable"
                          placeholder="Make All Non-Discountable"
                          type="radio"
                          checked={
                            discountableOption ===
                            'Do not all discountable'
                          }
                          onClick={() => {
                            setDiscountableOption(
                              discountableOptions.DONT_ALL_DISCOUNTABLE
                            )
                            setFieldValue('discountable', false)
                          }}
                        />
                      </span>
                    </div>
                    <div className="content-wrapper">
                      <span className="content-value">
                        <Field
                          component={RadioButton}
                          name="clearAllDiscountable"
                          placeholder="No Discounts Updates"
                          type="radio"
                          checked={
                            discountableOption ===
                            'No Discounts Updates'
                          }
                          onClick={() => {
                            setDiscountableOption(
                              discountableOptions.NO_DISCOUNTS_UPDATES
                            )
                            setFieldValue('discountable', '')
                            setFieldValue('clearAllDiscountable', '')
                          }}
                        />
                      </span>
                    </div>
                  </section>
                </div>
                <div className="btn-wrapper">
                  <button
                    className="btn-wrapper__action btn -update"
                    type="submit"
                    disabled={disableUpdateButton(dirty, errors)}
                  >
                    Save
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
        <SpySidebar settings={spySidebarSettings} />
      </div>
    </Modal>
  )
}

export default BulkForm
